<template>
  <div class="chat-composer" :class="{ 'chat-composer--focused': focused }">
    <textarea
      ref="textareaRef"
      v-model="model"
      class="chat-composer__input"
      :placeholder="placeholder"
      :maxlength="maxLength"
      rows="1"
      @input="autoresize"
      @keydown="handleKeydown"
      @focus="focused = true"
      @blur="focused = false"
    />

    <div class="chat-composer__actions">
      <span class="chat-composer__counter" :class="{ 'chat-composer__counter--warn': nearLimit }">
        {{ model.length }} / {{ maxLength }}
      </span>

      <button
        type="button"
        class="chat-composer__send"
        :disabled="!canSend"
        :aria-label="disabled ? 'Waiting for assistant' : 'Send message'"
        @click="handleSend"
      >
        <q-spinner v-if="disabled" size="14px" />
        <q-icon v-else name="arrow_upward" size="16px" />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';

const props = withDefaults(defineProps<{
  modelValue: string;
  disabled?: boolean;
  placeholder?: string;
  maxLength?: number;
}>(), {
  disabled: false,
  placeholder: 'Ask about a rule, or describe what should change…',
  maxLength: 8000
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void;
  (e: 'send'): void;
}>();

const textareaRef = ref<HTMLTextAreaElement | null>(null);
const focused = ref(false);

const model = computed({
  get: () => props.modelValue,
  set: (value: string) => emit('update:modelValue', value)
});

const canSend = computed(() => !props.disabled && model.value.trim().length > 0);

const nearLimit = computed(() => model.value.length > props.maxLength - 200);

function autoresize() {
  const el = textareaRef.value;
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
}

function handleKeydown(event: KeyboardEvent) {
  if (event.key === 'Enter' && !event.shiftKey) {
    event.preventDefault();
    if (canSend.value) handleSend();
  }
}

function handleSend() {
  if (!canSend.value) return;
  emit('send');
}

watch(() => props.modelValue, () => nextTick(autoresize));

defineExpose({
  focus: () => textareaRef.value?.focus()
});
</script>

<style scoped lang="scss">
.chat-composer {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 8px 10px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  transition: border-color 120ms ease, box-shadow 120ms ease;

  &--focused {
    border-color: #93b4f5;
    box-shadow: 0 0 0 3px rgba(59, 108, 243, 0.12);
  }

  &__input {
    flex: 1;
    min-width: 0;
    border: none;
    outline: none;
    resize: none;
    padding: 6px 4px;
    background: transparent;
    color: #111827;
    font-family: inherit;
    font-size: 14px;
    line-height: 1.5;
    max-height: 160px;

    &::placeholder { color: #9ca3af; }
    &:disabled { color: #6b7280; cursor: not-allowed; }
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
  }

  &__counter {
    font-size: 11px;
    color: #9ca3af;
    user-select: none;
    font-variant-numeric: tabular-nums;

    &--warn { color: #d97706; }
  }

  &__send {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    border: none;
    background: #3b6cf3;
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background-color 120ms ease, opacity 120ms ease, transform 80ms ease;

    &:hover:not(:disabled) { background: #2952d8; }
    &:active:not(:disabled) { transform: scale(0.96); }
    &:disabled {
      background: #d8dee9;
      cursor: not-allowed;
    }
  }
}

// Below ~360 px the char counter starts squeezing the send button. Hide it
// unless the user is approaching the limit — at that point it earns its space.
@container ra (max-width: 359px) {
  .chat-composer { padding: 6px 8px; gap: 6px; border-radius: 12px; }
  .chat-composer__counter:not(.chat-composer__counter--warn) { display: none; }
  .chat-composer__input { font-size: 13.5px; }
}
</style>
